import { useState } from "react";
import { newId, makeEconomy, fuelLogsForCar, type CanonicalStore, type Car, type TyreSet, type Tyre } from "../../core/index.ts";
import { BackRow, Modal, TextField, NumberField, SelectField, DateField, FormActions, parseNum } from "../forms/Modal.tsx";
import { epochToDateInput, dateInputToEpoch, longDate } from "../format.ts";

function durationLabel(sec: number): string {
  const days = Math.floor(sec / 86400);
  if (days < 30) return `${days} DAY${days === 1 ? "" : "S"}`;
  const months = Math.floor(days / 30);
  const remDays = days % 30;
  if (months < 12) return remDays > 0 ? `${months}M ${remDays}D` : `${months} MONTH${months === 1 ? "" : "S"}`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths > 0 ? `${years}Y ${remMonths}M` : `${years} YEAR${years === 1 ? "" : "S"}`;
}

function storageEpoch(ts: TyreSet): number | null {
  const log = ts.mountLog;
  if (!log || log.length === 0) {
    return ts.purchaseDate ?? ts.createdAt ?? null;
  }
  const last = log[log.length - 1]!;
  if (last.removedDate) return last.removedDate;
  if (last.removedOdometer != null) return last.removedDate ?? log[log.length - 2]?.mountedDate ?? null;
  return null; // still on car
}

export function TyresView({ store, car, save, onBack }: {
  store: CanonicalStore; car: Car; save: (s: CanonicalStore) => void; onBack: () => void;
}) {
  const sets = store.tyreSets.filter((t) => t.carId === car.id);
  const individualTyres = store.tyres.filter((t) => t.carId === car.id);
  const mounted = sets.find((s) => {
    const lm = s.mountLog?.[s.mountLog.length - 1];
    return lm != null && lm.removedOdometer == null;
  }) ?? null;
  const [editing, setEditing] = useState<TyreSet | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showSwap, setShowSwap] = useState(false);
  const [editingTyre, setEditingTyre] = useState<Tyre | null>(null);

  const logs = fuelLogsForCar(store.fuelLogs, car.id);
  const economy = makeEconomy(store.profile.unitSystem === "metric");
  const currentOdoKm = logs.reduce((m, l) => Math.max(m, l.odometer), 0);

  function tyreDistance(ts: TyreSet): number | null {
    const lastMount = ts.mountLog?.[ts.mountLog.length - 1];
    if (!lastMount) return null;
    const endOdo = lastMount.removedOdometer != null ? lastMount.removedOdometer : currentOdoKm;
    const dist = endOdo - lastMount.mountedOdometer;
    return dist >= 0 ? dist : 0;
  }

  if (showForm || editing) {
    return <TyreSetForm store={store} car={car} editing={editing ?? undefined} onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} currentOdoKm={currentOdoKm} economy={economy} />;
  }

  if (showSwap) {
    return <SwapSetsView store={store} car={car} save={save} onClose={() => setShowSwap(false)} currentOdoKm={currentOdoKm} economy={economy} />;
  }

  if (editingTyre) {
    return <TyreForm store={store} car={car} editing={editingTyre} onSave={save} onClose={() => setEditingTyre(null)} economy={economy} />;
  }

  const mountedDistKm = mounted ? tyreDistance(mounted) : null;
  const mountedDist = mountedDistKm != null ? economy.convertDistance(mountedDistKm) : null;

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="eyebrow amber" style={{ marginTop: 4 }}>TIRES · <span className="muted">{(car.make || car.model || "").toUpperCase()}</span></div>
        <div className="headline">RUBBER <span className="accent">sets.</span></div>

        {/* On-the-car summary */}
        <div className="panel" style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, borderColor: mounted ? "var(--lime)" : "var(--edge)" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mono muted" style={{ fontSize: 9, letterSpacing: 2 }}>ON THE CAR</div>
            <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: mounted ? "var(--cream)" : "var(--muted)" }}>
              {mounted ? (mounted.name || mounted.brand || "Set") : "No set mounted"}
            </div>
          </div>
          {mounted && mountedDist != null && mountedDist > 0 && (
            <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: "var(--lime)", flexShrink: 0 }}>
              {Math.round(mountedDist)} {economy.distanceLabel()}
            </div>
          )}
        </div>

        {sets.length > 0 && (
          <button className="btn primary" style={{ width: "100%", marginTop: 10 }} onClick={() => setShowSwap(true)}>⇄ SWAP SETS</button>
        )}

        {sets.length === 0 && <div className="panel mono muted" style={{ fontSize: 12, marginTop: 10 }}>No tire sets logged. Tap below to add one.</div>}

        <button className="btn ghost" style={{ fontSize: 11, marginTop: 10, marginBottom: 12 }} onClick={() => setShowForm(true)}>+ ADD NEW SET</button>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sets.map((ts) => {
            const lastMount = ts.mountLog?.[ts.mountLog.length - 1];
            const isOnCar = lastMount && lastMount.removedOdometer == null;
            const distKm = tyreDistance(ts);
            const dist = distKm != null ? economy.convertDistance(distKm) : null;
            const storageEpochSec = storageEpoch(ts);
            const inStorageFor = storageEpochSec != null && !isOnCar ? durationLabel(Date.now() / 1000 - storageEpochSec) : null;
            const specs = [ts.brand, ts.model, ts.size].filter(Boolean).join("  ·  ");
            const cpd = ts.purchasePrice != null && dist != null && dist > 0 ? ts.purchasePrice / dist : null;
            const dLabel = economy.distanceLabel().toUpperCase();
            return (
              <div key={ts.id} className="panel" style={{ cursor: "pointer" }} onClick={() => setEditing(ts)}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className="mono cream" style={{ fontSize: 13, flex: 1, minWidth: 0 }}>{ts.name || ts.brand || "UNKNOWN"}</div>
                  <span className="mono" style={{
                    fontSize: 8, letterSpacing: 1.2, padding: "3px 8px", borderRadius: 4, flexShrink: 0,
                    color: isOnCar ? "var(--lime)" : "var(--muted)",
                    background: isOnCar ? "rgba(182,255,60,0.12)" : "rgba(138,134,128,0.12)",
                    border: `1px solid ${isOnCar ? "rgba(182,255,60,0.4)" : "rgba(138,134,128,0.4)"}`,
                  }}>{isOnCar ? "ON CAR" : "IN STORAGE"}</span>
                  <span className="mono muted" style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>›</span>
                </div>
                {specs && <div className="mono" style={{ fontSize: 10, marginTop: 6, color: "var(--creamDim)" }}>{specs}</div>}
                <div style={{ display: "flex", gap: 18, marginTop: 8, flexWrap: "wrap" }}>
                  <Metric label="DISTANCE" value={dist != null && dist > 0 ? `${Math.round(dist)} ${dLabel}` : "—"} color="var(--cyan)" />
                  {cpd != null && <Metric label={`COST/${dLabel}`} value={`${cpd >= 1 ? cpd.toFixed(2) : cpd.toFixed(3)} ${store.profile.displayCurrency}`} color="var(--amber)" />}
                  {ts.currentTreadDepth != null && <Metric label="TREAD" value={`${ts.currentTreadDepth.toFixed(1)} mm`} color="var(--cream)" />}
                  {!isOnCar && inStorageFor && <Metric label="IN STORAGE" value={`~${inStorageFor}`} color="var(--muted)" />}
                </div>
              </div>
            );
          })}
        </div>

        {individualTyres.length > 0 && (
          <>
            <div className="eyebrow amber" style={{ marginTop: 22, marginBottom: 10 }}>
              INDIVIDUAL TIRES · <span className="muted">{individualTyres.length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {individualTyres.map((t) => (
                <div key={t.id} className="panel" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setEditingTyre(t)}>
                  <div>
                    <div className="mono cream" style={{ fontSize: 12 }}>{t.position ?? "—"}</div>
                    <div className="mono muted" style={{ fontSize: 10, marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {(t.brand || t.model) && <span>{t.brand ?? ""} {t.model ?? ""}</span>}
                      <span>{t.season ?? "—"} · {t.size ?? "—"}</span>
                      {t.installOdometer != null && <span>FITTED {Math.round(economy.convertDistance(t.installOdometer))} {economy.distanceLabel()}</span>}
                    </div>
                  </div>
                  {t.purchasePrice != null && (
                    <div className="mono" style={{ fontSize: 11, color: "var(--cream)", minWidth: 90, textAlign: "right" }}>
                      {store.profile.displayCurrency} {t.purchasePrice.toFixed(2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <div className="mono muted" style={{ fontSize: 8, letterSpacing: 1.2 }}>{label}</div>
      <div className="mono" style={{ fontSize: 12, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// Full-screen swap flow (no popup): pick a storage set to mount, or take the
// current one off. Uses the latest odometer and today's date.
function SwapSetsView({ store, car, save, onClose, currentOdoKm }: {
  store: CanonicalStore; car: Car; save: (s: CanonicalStore) => void; onClose: () => void;
  currentOdoKm: number; economy: ReturnType<typeof makeEconomy>;
}) {
  const sets = store.tyreSets.filter((t) => t.carId === car.id);
  const mounted = sets.find((s) => {
    const lm = s.mountLog?.[s.mountLog.length - 1];
    return lm != null && lm.removedOdometer == null;
  }) ?? null;
  const storage = sets.filter((s) => s.id !== mounted?.id);
  const now = () => Math.floor(Date.now() / 1000);

  function closeMounted(list: TyreSet[]): TyreSet[] {
    return list.map((t) => {
      const lm = t.mountLog?.[t.mountLog.length - 1];
      if (lm && lm.removedOdometer == null) {
        const log = [...(t.mountLog ?? [])];
        log[log.length - 1] = { ...lm, removedOdometer: currentOdoKm, removedDate: now() };
        return { ...t, mountLog: log };
      }
      return t;
    });
  }

  function mountSet(target: TyreSet) {
    let next = closeMounted(store.tyreSets);
    next = next.map((t) => t.id === target.id
      ? { ...t, mountLog: [...(t.mountLog ?? []), { id: newId(), mountedOdometer: currentOdoKm, mountedDate: now() }] }
      : t);
    save({ ...store, tyreSets: next });
    onClose();
  }

  function takeOff() {
    save({ ...store, tyreSets: closeMounted(store.tyreSets) });
    onClose();
  }

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onClose} />
        <div className="eyebrow amber" style={{ marginTop: 4 }}>SWAP · <span className="muted">{(car.make || car.model || "").toUpperCase()}</span></div>
        <div className="headline">SWAP <span className="accent">sets.</span></div>
        <div className="mono muted" style={{ fontSize: 11, marginTop: 8 }}>
          {mounted ? `${mounted.name || mounted.brand || "A set"} is on the car. ` : "No set is on the car. "}
          Choose a set to put on.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          {storage.map((s) => (
            <div key={s.id} className="panel" style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }} onClick={() => mountSet(s)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono cream" style={{ fontSize: 13 }}>{s.name || s.brand || "Set"}</div>
                <div className="mono muted" style={{ fontSize: 10, marginTop: 3 }}>{[s.brand, s.model, s.size].filter(Boolean).join("  ·  ") || (s.season ?? "—")}</div>
              </div>
              <span className="mono amber" style={{ fontSize: 10, letterSpacing: 1.4, flexShrink: 0 }}>PUT ON ›</span>
            </div>
          ))}
          {storage.length === 0 && <div className="panel mono muted" style={{ fontSize: 12 }}>No other sets in storage. Add another set first.</div>}
        </div>

        {mounted && (
          <button className="btn ghost" style={{ width: "100%", marginTop: 14, borderColor: "var(--redline)", color: "var(--redline)" }} onClick={takeOff}>
            TAKE OFF {(mounted.name || mounted.brand || "SET").toUpperCase()}
          </button>
        )}
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10,
  color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 13, padding: "10px 14px",
  boxSizing: "border-box",
};

export function TyreSetForm({ store, car, editing, onSave, onClose, currentOdoKm: odoKm, economy: eco }: {
  store: CanonicalStore; car: Car; editing?: TyreSet; onSave: (s: CanonicalStore) => void; onClose: () => void; currentOdoKm: number; economy: ReturnType<typeof makeEconomy>;
}) {
  const lastMount = editing?.mountLog?.[editing.mountLog.length - 1];
  const isOnCar = lastMount && lastMount.removedOdometer == null;

  const [name, setName] = useState(editing?.name ?? "");
  const [brand, setBrand] = useState(editing?.brand ?? "");
  const [model, setModel] = useState(editing?.model ?? "");
  const [size, setSize] = useState(editing?.size ?? "");
  const [season, setSeason] = useState(editing?.season ?? "summer");
  const [purchasePrice, setPurchasePrice] = useState(editing?.purchasePrice != null ? editing.purchasePrice.toFixed(2) : "");
  const [purchaseDate, setPurchaseDate] = useState(editing?.purchaseDate ? new Date(editing.purchaseDate * 1000).toISOString().slice(0, 10) : "");
  const [purchaseOdometer, setPurchaseOdometer] = useState(editing?.purchaseOdometer != null ? String(editing.purchaseOdometer) : "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [countPriceInSpend, setCountPriceInSpend] = useState(editing?.countPriceInSpend ?? false);
  const [mountOdo, setMountOdo] = useState(editing && isOnCar ? "" : String(Math.round(eco.convertDistance(odoKm))));
  const [mountDate, setMountDate] = useState(new Date().toISOString().slice(0, 10));
  const [removeOdo, setRemoveOdo] = useState(isOnCar ? String(Math.round(eco.convertDistance(odoKm))) : "");
  const [removeDate, setRemoveDate] = useState(new Date().toISOString().slice(0, 10));
  const currency = store.profile.displayCurrency;

  const editDistKm = lastMount ? Math.max(0, (isOnCar ? odoKm : (lastMount.removedOdometer ?? odoKm)) - lastMount.mountedOdometer) : null;
  const editDistStr = editDistKm != null ? `${Math.round(eco.convertDistance(editDistKm))} ${eco.distanceLabel().toUpperCase()}` : null;
  const editStoredEpoch = storageEpoch(editing!);
  const editInStorageFor = editStoredEpoch != null && !isOnCar ? durationLabel(Date.now() / 1000 - editStoredEpoch) : null;

  const makeSet = (overrides: Partial<TyreSet> = {}): TyreSet => ({
    id: editing?.id ?? newId(),
    carId: car.id,
    name: name || undefined,
    brand: brand || undefined,
    model: model || undefined,
    size: size || undefined,
    season: season || undefined,
    purchasePrice: purchasePrice ? Number(purchasePrice) : undefined,
    purchaseDate: purchaseDate ? new Date(purchaseDate + "T00:00:00Z").getTime() / 1000 : undefined,
    purchaseOdometer: purchaseOdometer ? Number(purchaseOdometer) : undefined,
    initialTreadDepth: editing?.initialTreadDepth,
    currentTreadDepth: editing?.currentTreadDepth,
    mountLog: editing?.mountLog,
    spendingId: editing?.spendingId,
    countPriceInSpend,
    notes: notes || undefined,
    createdAt: editing?.createdAt ?? Math.floor(Date.now() / 1000),
    ...overrides,
  });

  const save_ = () => {
    const set = makeSet();
    const existing = store.tyreSets.findIndex((t) => t.id === set.id);
    onSave({
      ...store,
      tyreSets: existing >= 0
        ? store.tyreSets.map((t) => t.id === set.id ? set : t)
        : [...store.tyreSets, set],
    });
    onClose();
  };

  const handleMount = () => {
    const entry = {
      id: newId(),
      // The input is prefilled and typed in the user's display unit, but
      // mountedOdometer is km on the wire (backup schema). Convert, as the
      // installOdometer field below already does.
      mountedOdometer: eco.toKm(Number(mountOdo)),
      mountedDate: mountDate ? new Date(mountDate + "T00:00:00Z").getTime() / 1000 : undefined,
    };
    const set = makeSet({ mountLog: [...(editing?.mountLog ?? []), entry] });
    const existing = store.tyreSets.findIndex((t) => t.id === set.id);
    onSave({
      ...store,
      tyreSets: existing >= 0
        ? store.tyreSets.map((t) => t.id === set.id ? set : t)
        : [...store.tyreSets, set],
    });
  };

  const handleRemove = () => {
    if (!lastMount) return;
    const updated = { ...lastMount, removedOdometer: eco.toKm(Number(removeOdo)), removedDate: removeDate ? new Date(removeDate + "T00:00:00Z").getTime() / 1000 : undefined };
    const set = makeSet({ mountLog: (editing?.mountLog ?? []).map((m) => m.id === lastMount.id ? updated : m) });
    const existing = store.tyreSets.findIndex((t) => t.id === set.id);
    onSave({
      ...store,
      tyreSets: existing >= 0
        ? store.tyreSets.map((t) => t.id === set.id ? set : t)
        : [...store.tyreSets, set],
    });
  };

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onClose} />
        <div className="screen-title">{editing ? "EDIT" : "NEW"} TIRE SET</div>
        <div className="eyebrow amber">{car.model}</div>

        {editing && (
          <div className="panel" style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center", borderColor: isOnCar ? "var(--cyan)" : "var(--edge)" }}>
            <div>
              <span className="mono" style={{ fontSize: 11, letterSpacing: 1.6, color: isOnCar ? "var(--cyan)" : "var(--muted)" }}>
                {isOnCar
                  ? `ON CAR FOR ~${editDistStr ?? "—"}`
                  : (editInStorageFor ? `IN STORAGE FOR ${editInStorageFor}` : "IN STORAGE")}
              </span>
              {lastMount && (
                <div className="mono muted" style={{ fontSize: 9, marginTop: 2 }}>
                  MOUNTED AT {Math.round(eco.convertDistance(lastMount.mountedOdometer))} {eco.distanceLabel()}
                </div>
              )}
            </div>
            {isOnCar ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, marginLeft: 12 }}>
                <input style={inp} type="number" placeholder="Odometer" value={removeOdo} onChange={(e) => setRemoveOdo(e.target.value)} />
                <input style={inp} type="date" value={removeDate} onChange={(e) => setRemoveDate(e.target.value)} />
                <button className="btn ghost" style={{ fontSize: 10, alignSelf: "flex-end" }} onClick={handleRemove}>REMOVE FROM CAR</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, marginLeft: 12 }}>
                <input style={inp} type="number" placeholder="Odometer" value={mountOdo} onChange={(e) => setMountOdo(e.target.value)} />
                <input style={inp} type="date" value={mountDate} onChange={(e) => setMountDate(e.target.value)} />
                <button className="btn ghost" style={{ fontSize: 10, alignSelf: "flex-end" }} onClick={handleMount}>MOUNT ON CAR</button>
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>NAME</label><input style={inp} placeholder="e.g. Winter 2024" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>BRAND</label><input style={inp} placeholder="Brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>MODEL</label><input style={inp} placeholder="Model" value={model} onChange={(e) => setModel(e.target.value)} />
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>SIZE</label><input style={inp} placeholder="e.g. 225/45R17" value={size} onChange={(e) => setSize(e.target.value)} />
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>SEASON</label><select style={inp} value={season} onChange={(e) => setSeason(e.target.value)}>
            <option value="summer">Summer</option>
            <option value="winter">Winter</option>
            <option value="all-season">All Season</option>
          </select>
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>PURCHASE PRICE ({currency})</label><input style={inp} type="number" step="0.01" placeholder="0.00" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} />
          <label className="mono" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 8, color: "var(--cream)", cursor: "pointer" }}>
            <input type="checkbox" checked={countPriceInSpend} onChange={(e) => setCountPriceInSpend(e.target.checked)} />
            Count set price in spend
          </label>
          <div className="mono muted" style={{ fontSize: 9, marginTop: -4 }}>Leave off if you already logged the individual tires as spendings — the set still shows by name.</div>
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>PURCHASE DATE</label><input style={inp} type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>ODOMETER AT PURCHASE (KM)</label><input style={inp} type="number" placeholder="0" value={purchaseOdometer} onChange={(e) => setPurchaseOdometer(e.target.value)} />
          <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>NOTES</label><input style={inp} placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

          <button className="btn primary" style={{ marginTop: 6 }} onClick={save_}>{editing ? "SAVE" : "ADD SET"}</button>
        </div>
      </div>
    </div>
  );
}

// Editor for a single (per-position) tyre. Reused by the Garage → Tires screen and
// the SPEND screen (individual tire lines link here). Mutates store.tyres directly;
// the ledger derives Tires spend from it, so price edits reflect immediately.
export function TyreForm({ store, car, editing, onSave, onClose, economy }: {
  store: CanonicalStore; car: Car; editing: Tyre; onSave: (s: CanonicalStore) => void; onClose: () => void; economy: ReturnType<typeof makeEconomy>;
}) {
  const [position, setPosition] = useState(editing.position ?? "");
  const [brand, setBrand] = useState(editing.brand ?? "");
  const [model, setModel] = useState(editing.model ?? "");
  const [size, setSize] = useState(editing.size ?? "");
  const [season, setSeason] = useState(editing.season ?? "summer");
  const [price, setPrice] = useState(editing.purchasePrice != null ? editing.purchasePrice.toFixed(2) : "");
  const [purchaseDate, setPurchaseDate] = useState(epochToDateInput(editing.purchaseDate ?? null));
  const [installOdo, setInstallOdo] = useState(editing.installOdometer != null ? String(Math.round(economy.convertDistance(editing.installOdometer))) : "");
  const [tread, setTread] = useState(editing.treadDepth != null ? String(editing.treadDepth) : "");
  const [notes, setNotes] = useState(editing.notes ?? "");
  const currency = store.profile.displayCurrency;
  const dl = economy.distanceLabel();

  function save_() {
    const updated: Tyre = {
      ...editing,
      position: position.trim() || undefined,
      brand: brand.trim() || undefined,
      model: model.trim() || undefined,
      size: size.trim() || undefined,
      season: season || undefined,
      purchasePrice: price ? parseNum(price) : null,
      purchaseDate: purchaseDate ? dateInputToEpoch(purchaseDate) : null,
      installOdometer: installOdo ? economy.toKm(parseNum(installOdo)) : null,
      treadDepth: tread ? parseNum(tread) : null,
      notes: notes.trim() || null,
    };
    onSave({ ...store, tyres: store.tyres.map((x) => (x.id === updated.id ? updated : x)) });
    onClose();
  }

  return (
    <Modal title="Edit tire" onClose={onClose}>
      <TextField label="Position" value={position} onChange={setPosition} placeholder="e.g. Front left" />
      <TextField label="Brand" value={brand} onChange={setBrand} />
      <TextField label="Model" value={model} onChange={setModel} />
      <TextField label="Size" value={size} onChange={setSize} placeholder="e.g. 225/45R17" />
      <SelectField label="Season" value={season} onChange={setSeason} options={["summer", "winter", "all-season"]} />
      <NumberField label={`Purchase price (${currency})`} value={price} onChange={setPrice} />
      <DateField label="Purchase date" value={purchaseDate} onChange={setPurchaseDate} />
      <NumberField label={`Fitted at (${dl})`} value={installOdo} onChange={setInstallOdo} />
      <NumberField label="Tread depth (mm)" value={tread} onChange={setTread} />
      <TextField label="Notes (optional)" value={notes} onChange={setNotes} />
      <FormActions
        canSave={true}
        onSave={save_}
        onDelete={() => { onSave({ ...store, tyres: store.tyres.filter((x) => x.id !== editing.id) }); onClose(); }}
      />
    </Modal>
  );
}
